import React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../utils/cn';

const typographyVariants = cva(
  "font-sans",
  {
    variants: {
      variant: {
        h1: "text-3xl font-bold leading-tight",
        h2: "text-2xl font-semibold leading-tight", 
        h3: "text-xl font-medium leading-tight",
        h4: "text-lg font-medium leading-tight",
        body: "text-base font-regular leading-normal",
        small: "text-sm font-regular leading-normal",
        caption: "text-xs font-regular leading-normal",
        label: "text-sm font-medium",
        link: "text-base font-regular text-primary-600 hover:text-primary-700 dark:text-accent-500 dark:hover:text-accent-400 underline cursor-pointer",
      },
      color: {
        primary: "text-gray-900 dark:text-dark-900",
        secondary: "text-gray-600 dark:text-dark-600", 
        tertiary: "text-gray-500 dark:text-dark-500",
        inverse: "text-white dark:text-gray-900",
        success: "text-green-600 dark:text-green-400",
        error: "text-red-600 dark:text-red-400",
        warning: "text-yellow-600 dark:text-yellow-400",
        info: "text-blue-600 dark:text-cyan-400",
        brand: "text-primary-600 dark:text-accent-500",
      },
      align: {
        left: "text-left",
        center: "text-center",
        right: "text-right",
        justify: "text-justify",
      },
    },
    defaultVariants: {
      variant: "body",
      color: "primary",
      align: "left",
    },
  }
);

const elementMap = {
  h1: 'h1',
  h2: 'h2', 
  h3: 'h3',
  h4: 'h4',
  body: 'p',
  small: 'p',
  caption: 'span',
  label: 'label',
  link: 'a',
};

export const Typography = React.forwardRef(({
  className,
  variant = "body",
  color,
  align,
  as,
  children,
  ...props
}, ref) => {
  const Element = as || elementMap[variant] || 'span';
  
  return (
    <Element
      ref={ref}
      className={cn(typographyVariants({ variant, color, align }), className)}
      {...props}
    >
      {children}
    </Element>
  );
});

Typography.displayName = "Typography";

// Convenient component variants
export const Heading1 = (props) => (
  <Typography variant="h1" {...props} />
);

export const Heading2 = (props) => (
  <Typography variant="h2" {...props} />
);

export const Heading3 = (props) => (
  <Typography variant="h3" {...props} />
);

export const Heading4 = (props) => (
  <Typography variant="h4" {...props} />
);

export const Body = (props) => (
  <Typography variant="body" {...props} />
);

export const Small = (props) => (
  <Typography variant="small" {...props} />
);

export const Caption = (props) => (
  <Typography variant="caption" {...props} />
);

export const Label = (props) => (
  <Typography variant="label" {...props} />
);

export const Link = (props) => (
  <Typography variant="link" {...props} />
); 